// src/components/workAccountability/RequirementsEditor.tsx
//
// Part 2 (Requirements): controls for EVERY backend-supported rule. Approval is
// shown disabled with "Coming Soon" (no approval workflow in Phase 1).

import type { RequirementLevel, TimeTrackingMode, TrackingMethod, WorkAccountabilityRules } from '../../types/workAccountability';
import { REQUIREMENT_LEVELS, TIME_TRACKING_MODES } from './waFormat';
import { Segmented, Toggle } from './waControls';

interface Props {
  rules: WorkAccountabilityRules;
  onChange: (next: WorkAccountabilityRules) => void;
  disabled?: boolean;
}

export default function RequirementsEditor({ rules, onChange, disabled }: Props) {
  const set = (patch: Partial<WorkAccountabilityRules>) => onChange({ ...rules, ...patch });

  const toggleMethod = (m: TrackingMethod) => {
    const has = rules.trackingMethod.allowed.includes(m);
    const next = has ? rules.trackingMethod.allowed.filter((x) => x !== m) : [...rules.trackingMethod.allowed, m];
    if (next.length === 0) return; // never empty
    set({ trackingMethod: { allowed: next } });
  };

  return (
    <div className="space-y-5">
      <div>
        <Segmented<TimeTrackingMode>
          label="Time Tracking"
          value={rules.timeTracking.mode}
          options={TIME_TRACKING_MODES}
          onChange={(mode) => set({ timeTracking: { mode } })}
          disabled={disabled}
          idPrefix="wa-timeTracking"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Allowed Methods</label>
        <div className="inline-flex flex-wrap gap-2">
          {(['manual', 'completion'] as TrackingMethod[]).map((m) => {
            const selected = rules.trackingMethod.allowed.includes(m);
            return (
              <button
                key={m}
                type="button"
                role="checkbox"
                aria-checked={selected}
                disabled={disabled}
                onClick={() => toggleMethod(m)}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 ${
                  selected ? 'bg-blue-50 border-blue-300 text-blue-800' : 'bg-white border-gray-300 text-gray-600'
                }`}
              >
                {m === 'manual' ? 'Manual' : 'Completion Adjustment'}
              </button>
            );
          })}
        </div>
      </div>

      <Segmented<RequirementLevel>
        label="Completion Note"
        value={rules.completionNote}
        options={REQUIREMENT_LEVELS}
        onChange={(completionNote) => set({ completionNote })}
        disabled={disabled}
        idPrefix="wa-completionNote"
      />

      <Segmented<RequirementLevel>
        label="Proof Of Done"
        value={rules.proofOfDone}
        options={REQUIREMENT_LEVELS}
        onChange={(proofOfDone) => set({ proofOfDone })}
        disabled={disabled}
        idPrefix="wa-proofOfDone"
      />

      <div className="space-y-3 border-t border-gray-100 pt-4">
        <Toggle
          label="Require Estimate"
          description="A task must have an estimate to be completed."
          checked={rules.estimate.required}
          onChange={(v) => set({ estimate: { required: v } })}
          disabled={disabled}
        />
        <Toggle
          label="Variance Explanation"
          description="Require an explanation when actual time exceeds the threshold over the estimate."
          checked={rules.variance.explanationRequired}
          onChange={(v) => set({ variance: { ...rules.variance, explanationRequired: v } })}
          disabled={disabled}
        />
        {rules.variance.explanationRequired && (
          <div className="pl-1">
            <label htmlFor="wa-threshold" className="block text-sm font-medium text-gray-700 mb-1">
              Threshold %
            </label>
            <input
              id="wa-threshold"
              type="number"
              min={0}
              max={1000}
              value={rules.variance.thresholdPct}
              disabled={disabled}
              onChange={(e) => {
                const n = Math.max(0, Math.min(1000, Math.floor(Number(e.target.value) || 0)));
                set({ variance: { ...rules.variance, thresholdPct: n } });
              }}
              className="w-28 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        )}
        <Toggle
          label="Require Time Before Completion"
          description="Some time must be logged before a task can be completed."
          checked={rules.requireTimeBeforeCompletion}
          onChange={(v) => set({ requireTimeBeforeCompletion: v })}
          disabled={disabled}
        />
        <Toggle
          label="Require Edit Reason"
          description="Corrections to time entries must include a reason."
          checked={rules.editReasonRequired}
          onChange={(v) => set({ editReasonRequired: v })}
          disabled={disabled}
        />
        <Toggle
          label="Allow Backdated Entry"
          description="Permit logging time for a past date."
          checked={rules.allowBackdatedEntry}
          onChange={(v) => set({ allowBackdatedEntry: v })}
          disabled={disabled}
        />
      </div>

      {/* Approval — RESERVED. No workflow in Phase 1. */}
      <div className="border-t border-gray-100 pt-4 opacity-60" aria-disabled="true">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-medium text-gray-800">Approval</div>
            <div className="text-xs text-gray-500">Manager approval before completion.</div>
          </div>
          <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
            Coming Soon
          </span>
        </div>
      </div>
    </div>
  );
}
